import React, { useState } from "react";
import { RadioButton, BlockStack } from "@medentic/polaris-react";
import dayjs from "dayjs";
import DatePickerField from "./DatePickerField";
import { useTranslation } from "react-i18next";

const DB_DATE_FORMAT_STRING = "YYYY-MM-DD";

interface DateRangeListProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

const getRange = (option: string): { start: string; end: string } => {
  const today = dayjs();
  switch (option) {
    case "today":
      return {
        start: today.format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
    case "last7":
      return {
        start: today.subtract(6, "day").format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
    case "last30":
      return {
        start: today.subtract(29, "day").format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
    case "last90":
      return {
        start: today.subtract(89, "day").format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
    case "last12m":
      return {
        start: today
          .subtract(1, "year")
          .add(1, "day")
          .format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
    default:
      return {
        start: today.format(DB_DATE_FORMAT_STRING),
        end: today.format(DB_DATE_FORMAT_STRING),
      };
  }
};

const parseValue = (value: string): { start: string; end: string } => {
  const [start, end] = value.split("|");
  return { start, end: end || start };
};

const DateRangeList: React.FC<DateRangeListProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const { t } = useTranslation();

  const RADIO_OPTIONS = [
    { label: t("dateRange.today"), value: "today" },
    { label: t("dateRange.last7"), value: "last7" },
    { label: t("dateRange.last30"), value: "last30" },
    { label: t("dateRange.last90"), value: "last90" },
    { label: t("dateRange.last12m"), value: "last12m" },
    { label: t("dateRange.custom"), value: "custom" },
  ];

  const inferSelected = (val: string) => {
    const { start, end } = parseValue(val);
    const today = dayjs().format(DB_DATE_FORMAT_STRING);
    if (start === today && end === today) return "today";
    if (
      start === dayjs().subtract(6, "day").format(DB_DATE_FORMAT_STRING) &&
      end === today
    )
      return "last7";
    if (
      start === dayjs().subtract(29, "day").format(DB_DATE_FORMAT_STRING) &&
      end === today
    )
      return "last30";
    if (
      start === dayjs().subtract(89, "day").format(DB_DATE_FORMAT_STRING) &&
      end === today
    )
      return "last90";
    if (
      start ===
        dayjs()
          .subtract(1, "year")
          .add(1, "day")
          .format(DB_DATE_FORMAT_STRING) &&
      end === today
    )
      return "last12m";
    return "custom";
  };

  const [selected, setSelected] = useState<string>(() => inferSelected(value));
  const { start, end } = parseValue(
    value || getRange("today").start + "|" + getRange("today").end
  );
  const [customStart, setCustomStart] = useState<string>(start);
  const [customEnd, setCustomEnd] = useState<string>(end);

  // Sync selected state with value
  React.useEffect(() => {
    setSelected(inferSelected(value));
    const { start, end } = parseValue(
      value || getRange("today").start + "|" + getRange("today").end
    );
    setCustomStart(start);
    setCustomEnd(end);
  }, [value]);

  const handleRadioChange = (option: string) => {
    setSelected(option);
    if (option !== "custom") {
      const range = getRange(option);
      onChange(`${range.start}|${range.end}`);
    }
  };

  const handleCustomStart = (val: string) => {
    setCustomStart(val);
  };
  const handleCustomEnd = (val: string) => {
    setCustomEnd(val);
  };

  const handleCustomKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" && customStart && customEnd) {
      onChange(`${customStart}|${customEnd}`);
    }
  };

  return (
    <BlockStack gap="200">
      {RADIO_OPTIONS.map((opt) => (
        <RadioButton
          key={opt.value}
          label={opt.label}
          checked={selected === opt.value}
          id={opt.value}
          name="date-range-list"
          onChange={() => handleRadioChange(opt.value)}
          disabled={disabled}
        />
      ))}
      {selected === "custom" && (
        <div
          style={{ paddingLeft: 24 }}
          onKeyDown={handleCustomKeyDown}
          tabIndex={0}
        >
          <DatePickerField
            label={t("dateRange.starting")}
            value={customStart}
            onChange={handleCustomStart}
            onInputChange={handleCustomStart}
            disabled={disabled}
            isRequired={false}
          />
          <DatePickerField
            label={t("dateRange.ending")}
            value={customEnd}
            onChange={handleCustomEnd}
            onInputChange={handleCustomEnd}
            disabled={disabled}
            isRequired={false}
          />
        </div>
      )}
    </BlockStack>
  );
};

export default DateRangeList;
